import type ActiveStoreDto from "~/models/ActiveStoreDto";

import type StoreDetailDto from "~/models/StoreDetailDto";
import type StoreDto from "~/models/StoreDto";
import ld from "lodash";
import { type AnybillResult, AnybillResultHelper } from "~/additionalModels/AnybillResponse";
import AccessGroupCustomerEnum from "~/models/AccessGroupCustomerEnum";
import CreateStoreDetailDto from "~/models/CreateStoreDetailDto";
import CreateStoreDto from "~/models/CreateStoreDto";
import { StoreProvider } from "~/provider/StoreProvider";
import { VendorCustomerProvider } from "~/provider/VendorCustomerProvider";

// TODO Rework this with provider logic
export const useStoreModule = defineRequestStore("store", () => {
  const userModule = useUserModule();
  const stores = ref<StoreDto[] | null>(null);
  const activeStores = ref<ActiveStoreDto[] | null>(null);
  const unavailable = ref<boolean | null>(null);
  const loading = ref(false);
  const skip = ref(0);
  // indicates wheter populateAll is running or not
  const allLoading = ref(false);
  const totalStoreCount = ref(0);

  // local StoreModule-dialog
  const currentStore = ref<StoreDto | null>(null);
  const storeCreationLogo = ref<File | null>(null);
  const isEdit = ref(false);
  const initialized = computed(() => {
    return !!stores.value && !!activeStores.value;
  });

  const hasStores = computed(() => {
    // If stores haven't been loaded yet, don't assume incomplete
    if (stores.value === null)
      return true;
    return (stores?.value ?? []).length > 0 || unavailable.value;
  });

  const _hasReadrights: any = computed<boolean>(() => {
    return (userModule.userRights?.get(AccessGroupCustomerEnum.Store) ?? 0) >= 1;
  });

  function setSortedStores(partnerRestriction: any) {
    const notSelected = stores.value?.filter(s => !partnerRestriction?.storeIds?.includes(s.id));
    const selected = stores.value?.filter(s => partnerRestriction?.storeIds?.includes(s.id));
    if (selected?.length === 0)
      stores.value?.sort((a: any, b) => a.storeDetail.displayName.localeCompare(b.storeDetail.displayName));
    // this.SET_STORES(this.stores);

    if (selected && selected?.length !== 0 && notSelected && notSelected?.length !== 0) {
      selected?.sort((a: any, b) => a.storeDetail.displayName.localeCompare(b.storeDetail.displayName));
      notSelected?.sort((a: any, b) => a.storeDetail.displayName.localeCompare(b.storeDetail.displayName));
      stores.value = [...selected, ...notSelected];
    }
  }

  function reset() {
    stores.value = null;
    unavailable.value = false;
  }

  async function populate({
    skip = 0,
    preserve = false,
  }: {
    skip?: number;
    preserve?: boolean;
  } = { skip: 0, preserve: false }) {
    if (stores.value?.length === totalStoreCount.value)
      return; // leave early if already populated by populateAll

    if (!_hasReadrights.value) {
      unavailable.value = true;
      return;
    }
    loading.value = true;

    // get active stores
    // const activeStores = await neconcatw StoreActivestoresService().get(`${skip}`, "100");
    const activeStoresRes = await useTypedFetch<AnybillResult<ActiveStoreDto[]>>(`/storeActivestoresService/get?skip=${skip}&take=100`);
    if (activeStoresRes.success)
      activeStores.value = activeStoresRes.value;

    // Todo: process setSelectedIds in BillStatisticsModule
    // if (!preserve) {
    //   totalStoreCount.value = _totalCount;
    //   stores.value = _stores;
    // }
    // else {
    //   _summarizeStores(_stores);
    // }
    loading.value = false;
  }

  async function populateAllStores() {
    allLoading.value = true;

    const take = 200;
    // Don't reset stores.value if it's already populated to prevent UI flickering
    const wasAlreadyPopulated = stores.value && stores.value.length > 0;
    if (!wasAlreadyPopulated) {
      stores.value = [];
    }

    const firstBatch = await StoreProvider.instance.getStores({ skip: 0, take });
    if (firstBatch.success) {
      const totalCount = firstBatch.headers?.totalCount ?? 0;
      stores.value = firstBatch.value;

      if (totalCount > take) {
        for (let i = take; i < totalCount; i += take) {
          const batch = await StoreProvider.instance.getStores({ skip: i, take });
          if (batch.success) {
            stores.value = stores.value?.concat(batch.value);
          }
          else {
            await AnybillLogger.instance.error(
              "Population call failed for getting stores",
              batch instanceof Error ? batch : new Error(batch.toString()),
              "StoreModule",
            );
          }
        }
      }
    }
    else {
      await AnybillLogger.instance.error(
        "Population call failed for getting stores",
        firstBatch instanceof Error ? firstBatch : new Error(firstBatch.toString()),
        "StoreModule",
      );
    }

    allLoading.value = false;
  }

  async function getAllStores() {
    allLoading.value = true;

    const take = 200;
    let storesToReturn: StoreDto[] = [];

    const firstBatch = await StoreProvider.instance.getStores({ skip: 0, take });
    if (firstBatch.success) {
      const totalCount = firstBatch.headers?.totalCount ?? 0;
      storesToReturn = firstBatch.value;

      if (totalCount > take) {
        for (let i = take; i < totalCount; i += take) {
          const batch = await StoreProvider.instance.getStores({ skip: i, take });
          if (batch.success) {
            storesToReturn = storesToReturn?.concat(batch.value);
          }
        }
      }
    }

    return storesToReturn;
  }

  async function createStore(createStoreDto: CreateStoreDto, vendorCustomerId: string | undefined, image: File | null): Promise<AnybillResult<void>> {
    let storeId;
    let res;
    if (vendorCustomerId) {
      const sanitizedDto = new CreateStoreDto({
        vendorCustomerStoreId: createStoreDto.vendorCustomerStoreId,
        storeDetail: _buildStoreDetailPayload(createStoreDto.storeDetail),
      });
      res = await VendorCustomerProvider.instance.createStore(vendorCustomerId, sanitizedDto);

      if (res.success) {
        const storeId = res.value.id;
        let hasErrors = false;

        if (image) {
          const iconUploaded = await _handleLogoUpload(vendorCustomerId, storeId, image);
          if (!iconUploaded)
            hasErrors = true;
        }

        const object = await VendorCustomerProvider.instance.getSingleStore(vendorCustomerId, storeId);

        if (object.success) {
          _summarizeStores([object.value]);
          totalStoreCount.value++;
        }
        else {
          await AnybillLogger.instance.warn(
            "Population call failed after creating a store",
            res instanceof Error ? res : new Error(res.toString()),
            "StoreModule",
          );
          hasErrors = true;
        }

        storeCreationLogo.value = null;
        currentStore.value = null;
        return hasErrors
          ? AnybillResultHelper.failure(null, 0, null)
          : AnybillResultHelper.success(void {}, 200);
      }
      else {
        await AnybillLogger.instance.error(
          "POST call failed during store creation",
          res instanceof Error ? res : new Error(res.toString()),
          "StoreModule",
        );
        return AnybillResultHelper.failure(res.errorMessage, 400, res.errorCode);
      }
    }
    else {
      const sanitizedDto = new CreateStoreDto({
        vendorCustomerStoreId: createStoreDto.vendorCustomerStoreId,
        storeDetail: _buildStoreDetailPayload(createStoreDto.storeDetail),
      });
      res = await useTypedFetch<AnybillResult<StoreDto>>("/storeService/post", sanitizedDto);

      if (res.success) {
        storeId = res.value.id;
        let hasErrors = false;

        // add companylogo
        if (image) {
          const iconUploaded = await _handleLogoUpload(undefined, storeId, image);
          if (!iconUploaded)
            hasErrors = true;
        }

        // update storelist
        const single = await useTypedFetch<AnybillResult<StoreDto>>("/storeSpecificService/get", { storeId });
        if (single.success) {
          _summarizeStores([single.value]);
        }
        else {
          await AnybillLogger.instance.warn(
            "Population call failed after creating a store",
            res instanceof Error ? res : new Error(res.toString()),
            "StoreModule",
          );
          hasErrors = true;
        }

        totalStoreCount.value++;
        return hasErrors
          ? AnybillResultHelper.failure(null, 0, null)
          : AnybillResultHelper.success(void {}, 200);
      }
      else {
        await AnybillLogger.instance.error(
          "POST call failed during store addition",
          res instanceof Error ? res : new Error(res.toString()),
          "StoreModule",
        );
        return AnybillResultHelper.failure(null, 0, null);
      }
    }
  }

  /**
   * add store data
   * If id is set cusstomer is activated
   */
  async function addStore({ id, image }: { id: string | undefined | null; image: File | null }) {
    let storeId = "";
    let res: any;
    if (!currentStore.value)
      return;
    const sanitizedStore = {
      ...ld.cloneDeep(currentStore.value),
      storeDetail: _buildStoreDetailPayload(currentStore.value.storeDetail),
    };
    // create store
    if (id) {
      res = await useTypedFetch<AnybillResult<StoreDto>>("/vendorcustomerSpecificStoreService/post", {
        body: sanitizedStore,
        vendorCustomerId: id,
      });
    }
    else {
      res = await useTypedFetch<AnybillResult<StoreDto>>("/storeService/post", sanitizedStore);
    }
    if (res.success) { storeId = res!.value.id; }
    else {
      await AnybillLogger.instance.error(
        "POST call failed during store addition",
        res instanceof Error ? res : new Error(res.toString()),
        "StoreModule",
      );
      return res;
    }

    // get updated store list
    let single: any;
    if (id) {
      single = await useTypedFetch<AnybillResult<StoreDto>>("/vendorcustomerSpecificStoreSpecificService/get", {
        vendorCustomerId: id,
        storeId,
      });
    }
    else {
      single = await useTypedFetch<AnybillResult<StoreDto>>("/storeSpecificService/get", storeId);
    }

    if (single.success) {
      stores.value!.push(res.value);
      _summarizeStores(single.value);
      totalStoreCount.value++;
    }
    else {
      await AnybillLogger.instance.error(
        "Population call of a single store failed",
        new Error(single ?? "unknown"),
        "StoreModule",
      );
    }
  }

  function _buildStoreDetailPayload(detail: StoreDetailDto | CreateStoreDetailDto | null | undefined): CreateStoreDetailDto {
    const payloadSource = detail ? ld.cloneDeep(detail) : {};
    const payload = new CreateStoreDetailDto(payloadSource as Partial<CreateStoreDetailDto>);
    Reflect.deleteProperty(payload as Record<string, unknown>, "iconResource");
    return payload;
  }

  /**
   * Updates store details via vendor customer API
   */
  async function _updateStoreViaVendorCustomer(vendorCustomerId: string, storeId: string): Promise<AnybillResult<StoreDto>> {
    const payload = _buildStoreDetailPayload(currentStore.value?.storeDetail ?? undefined);
    return await VendorCustomerProvider.instance.updateStore(
      vendorCustomerId,
      storeId,
      payload,
    );
  }

  /**
   * Updates store details directly
   */
  async function _updateStoreDirectly(storeId: string): Promise<boolean> {
    try {
      const payload = _buildStoreDetailPayload(currentStore.value?.storeDetail ?? undefined);
      await useTypedFetch("/storeSpecificService/put", {
        body: payload,
        storeId,
      });
      return true;
    }
    catch (error) {
      await AnybillLogger.instance.error(
        `Update call for store ${storeId}`,
        error instanceof Error ? error : new Error(error as any ?? "unknown"),
        "StoreModule",
      );
      return false;
    }
  }

  /**
   * Handles logo upload for a store
   */
  async function _handleLogoUpload(vendorCustomerId: string | undefined | null, storeId: string, logo: File): Promise<boolean> {
    const result = vendorCustomerId
      ? await VendorCustomerProvider.instance.uploadStoreIcon(vendorCustomerId, storeId, logo)
      : await StoreProvider.instance.uploadStoreIcon(storeId, logo);
    if (!result.success) {
      await AnybillLogger.instance.error(
        `Error uploading store icon for store ${storeId}`,
        new Error(result.errorMessage ?? "unknown error"),
        "StoreModule",
      );
      return false;
    }
    return true;
  }

  /**
   * Handles logo deletion for a store
   */
  async function _handleLogoDeletion(vendorCustomerId: string | undefined | null, storeId: string): Promise<boolean> {
    const result = vendorCustomerId
      ? await VendorCustomerProvider.instance.deleteStoreIcon(vendorCustomerId, storeId)
      : await StoreProvider.instance.deleteStoreIcon(storeId);
    if (!result.success) {
      await AnybillLogger.instance.error(
        `Error deleting store icon for store ${storeId}`,
        new Error(result.errorMessage ?? "unknown error"),
        "StoreModule",
      );
      return false;
    }
    return true;
  }

  /**
   * Refreshes store data from the server
   */
  async function _refreshStoreData(storeId: string): Promise<void> {
    const refreshedStore = await useTypedFetch<AnybillResult<StoreDto>>("/storeSpecificService/get", { storeId });
    if (refreshedStore.success) {
      _updateStore(refreshedStore.value);
    }
  }

  /**
   * Update store data
   * @param params - Update parameters
   * @param params.id - Vendor customer ID (required for activated customers)
   * @param params.logo - New logo file to upload, or null to potentially delete existing logo
   */
  async function updateStore({ id, logo }: { id: string | undefined | null; logo: File | null }): Promise<AnybillResult<void>> {
    const storeId = currentStore.value?.id;
    if (!storeId) {
      throw new Error("Store ID is required");
    }

    let hasErrors = false;

    // Step 1: Update store details
    if (id) {
      // Path A: Update via vendor customer (for activated customers)
      const updateResult = await _updateStoreViaVendorCustomer(id, storeId);
      if (updateResult.success) {
        _updateStore(updateResult.value);
      }
      else {
        await AnybillLogger.instance.error(
          `Error updating store ${storeId} via vendor customer ${id}`,
          new Error(`[${updateResult.errorCode}] ${updateResult.errorMessage ?? "missing message"}`),
          "StoreModule",
        );
        hasErrors = true;
      }
    }
    else {
      // Path B: Update directly (for non-activated stores)
      const success = await _updateStoreDirectly(storeId);
      if (!success) {
        hasErrors = true;
      }
    }
    // Step 2: Handle logo upload/deletion
    if (logo) {
      // Upload new logo
      const success = await _handleLogoUpload(id, storeId, logo);
      if (!success) {
        hasErrors = true;
      }
    }
    else if (!currentStore.value!.storeDetail.iconResource) {
      // Delete logo if no new logo provided and no existing icon resource
      const success = await _handleLogoDeletion(id, storeId);
      if (!success) {
        hasErrors = true;
      }
    }

    // Step 3: Refresh store data (only for non-vendor customer path)
    if (!id) {
      await _refreshStoreData(storeId);
    }

    // Step 4: Clean up
    storeCreationLogo.value = null;
    currentStore.value = null;

    return hasErrors
      ? AnybillResultHelper.failure(null, 0, null)
      : AnybillResultHelper.success(void {}, 200);
  }

  async function removeStore({ store, id }: { store: StoreDto; id: string | undefined | null }) {
    const storeId = store.id;
    try {
      if (id)
        await useTypedFetch("/vendorcustomerSpecificStoreService/delete", { body: store, vendorCustomerId: id });

      else
        await useTypedFetch("/storeService/delete", { storeId });
    }
    catch (error) {
      await AnybillLogger.instance.error(
        `Error called during deletion of store ${store.id}`,
        error instanceof Error ? error : new Error(error as any ?? "unknown"),
        "StoreModule",
      );
    }
    _removeStore(store);
    _decreaseStoreCount();
  }

  function _updateStore(store: StoreDto) {
    const clone = ld.cloneDeep(stores.value);
    if (!clone)
      return;
    const toReplaceInd = clone!.findIndex(c => c.id === store.id);
    clone![toReplaceInd] = store;
    stores.value = clone;
  }

  function _removeStore(store: StoreDto) {
    if (!stores.value)
      return;
    stores.value = stores.value!.filter(c => c.id !== store.id);
  }

  function _decreaseStoreCount() {
    totalStoreCount.value--;
  }

  // Todo: create computed totalStores
  function _summarizeStores(_stores: StoreDto[] | null) {
    if (stores.value === null || stores.value === undefined)
      stores.value = [];

    stores.value = stores.value?.concat(_stores ?? []);
  }

  return {
    populate,
    populateAllStores,
    addStore,
    createStore,
    setSortedStores,
    getAllStores,
    updateStore,
    removeStore,
    reset,
    initialized,
    storeCreationLogo,
    hasStores,
    stores,
    activeStores,
    unavailable,
    loading,
    // indicates wheter populateAll is running or not
    allLoading,
    totalStoreCount,
    currentStore,
    isEdit,
  };
});

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useStoreModule, import.meta.hot));
